/**
 * Ordering for the manager-maintained lists.
 *
 * Projects and tags are the same kind of thing — a short list somebody
 * arranges and everybody else reads — so they order the same way and share
 * this implementation rather than each carrying a copy that can drift.
 */

/** Anything a manager arranges by hand. */
export interface Ordered {
  id: string
  name: string
  /** Ascending; managers control it. Ties broken by name. */
  sortOrder: number
}

/**
 * Ordering used everywhere one of these lists is shown.
 *
 * A non-numeric sortOrder sorts last rather than making the comparator
 * return NaN, which would leave the whole list in an arbitrary order — a bug
 * the project list actually had, after a migration left the field undefined
 * on some rows.
 */
export function byOrder(a: Ordered, b: Ordered): number {
  const order = (x: Ordered) =>
    Number.isFinite(x.sortOrder) ? x.sortOrder : Number.MAX_SAFE_INTEGER
  const [x, y] = [order(a), order(b)]
  return x === y ? a.name.localeCompare(b.name) : x - y
}

/**
 * The full id order after moving one item to another's position.
 *
 * The reorder endpoints take every id in one list, so archived items are
 * appended unchanged — they are not shown in the sortable list, and leaving
 * them out would silently reshuffle them.
 *
 * Kept pure and separate from the drag library so the order actually sent to
 * the API can be tested without simulating a drag.
 */
export function reorderedIds(
  active: readonly { id: string }[],
  archived: readonly { id: string }[],
  fromId: string,
  toId: string,
): string[] {
  const ids = active.map((item) => item.id)
  const from = ids.indexOf(fromId)
  const to = ids.indexOf(toId)

  // An unknown id, or a drop back onto the starting position, changes nothing.
  if (from === -1 || to === -1 || from === to) {
    return [...ids, ...archived.map((item) => item.id)]
  }

  ids.splice(to, 0, ids.splice(from, 1)[0])
  return [...ids, ...archived.map((item) => item.id)]
}
